import React, { useState, useEffect } from "react";
import { entities, getDisplayMessage, requiresUploadBlobAuth, fetchUploadBlob } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Search, AlertTriangle, CheckCircle2, XCircle, FileText, Download, Trash2 } from "lucide-react";
import DocumentoCard from "../components/documentos/DocumentoCard";
import DocumentoForm from "../components/documentos/DocumentoForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogContent,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/AuthContext";
export default function DocumentosPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin" || user?.perfil === "admin" || user?.perfil === "super_admin";
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroCategoria, setFiltroCategoria] = useState("todos");
  const [advancedByTipo, setAdvancedByTipo] = useState({});

  // Capturar filtro da URL
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const statusParam = urlParams.get('status');
    if (statusParam && ['vencido', 'proximo_vencimento', 'valido'].includes(statusParam)) {
      setFiltroStatus(statusParam);
    }
  }, []);

  const [dialogAberto, setDialogAberto] = useState(false);
  const [documentoEditando, setDocumentoEditando] = useState(null);
  const [documentosSelecionados, setDocumentosSelecionados] = useState([]);
  const [downloadingBatch, setDownloadingBatch] = useState(false);
  const [dialogExclusaoAberto, setDialogExclusaoAberto] = useState(false);

  const { data: documentos = [], isLoading } = useQuery({
    queryKey: ['documentos'],
    queryFn: async () => {
      const data = await entities.Documento.list();
      return Array.isArray(data) ? data : [];
    }
  });

  const safeDocumentos = Array.isArray(documentos) ? documentos : [];
  const { data: tiposCatalogo = [] } = useQuery({
    queryKey: ["documentos", "tipos-catalogo"],
    queryFn: () => entities.Documento.tiposCatalogo(),
  });
  const { data: renovacaoConfigs = [] } = useQuery({
    queryKey: ["documentos", "renovacao-configs"],
    queryFn: () => entities.Documento.renovacaoConfigs(),
  });
  const { data: renovacaoExecucoes = [] } = useQuery({
    queryKey: ["documentos", "renovacao-execucoes"],
    queryFn: () => entities.Documento.renovacaoExecucoes(20),
    refetchInterval: 30000,
  });

  const autoTiposCatalogo = Array.isArray(tiposCatalogo)
    ? tiposCatalogo.filter((item) => item?.modoControle === "auto_renovavel")
    : [];

  const updateRenovacaoConfigMutation = useMutation({
    mutationFn: async ({ documentoTipoId, payload }) =>
      entities.Documento.atualizarRenovacaoConfig(documentoTipoId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documentos", "renovacao-configs"] });
      toast.success("Configuração de renovação atualizada.");
    },
    onError: (err) => {
      toast.error(getDisplayMessage(err, "Não foi possível atualizar a configuração de renovação."));
    },
  });

  const dispararRenovacaoMutation = useMutation({
    mutationFn: async () => entities.Documento.dispararRenovacaoAutomatica(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["documentos", "renovacao-execucoes"] });
      queryClient.invalidateQueries({ queryKey: ["documentos"] });
      toast.success(`Ciclo disparado. Criadas: ${result?.criadas ?? 0} | Enfileiradas: ${result?.enfileiradas ?? 0}`);
    },
    onError: (err) => {
      toast.error(getDisplayMessage(err, "Falha ao disparar renovação automática."));
    },
  });

  const reprocessarExecucaoMutation = useMutation({
    mutationFn: async (id) => entities.Documento.reprocessarRenovacaoExecucao(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documentos", "renovacao-execucoes"] });
      toast.success("Execução reenfileirada.");
    },
    onError: (err) => {
      toast.error(getDisplayMessage(err, "Falha ao reenfileirar execução."));
    },
  });

  // Disparar notificações de vencimento (10 dias antes) ao carregar a página
  useEffect(() => {
    entities.Documento.notificarVencimentos().catch(() => {});
  }, []);

  // Garantir sincronização de status no backend (útil para documentos importados/legados)
  useEffect(() => {
    const sincronizarStatus = async () => {
      await entities.Documento.sincronizarStatus().catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['documentos'] });
    };
    if (safeDocumentos.length > 0) {
      sincronizarStatus();
    }
  }, [safeDocumentos.length, queryClient]);

  const createDocumentoMutation = useMutation({
    mutationFn: async (data) => {
      await entities.Documento.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentos'] });
      setDialogAberto(false);
      setDocumentoEditando(null);
      toast.success('Documento salvo com sucesso.');
    },
    onError: (err) => {
      toast.error(getDisplayMessage(err, 'Não foi possível salvar o documento. Tente novamente.'));
    }
  });

  const updateDocumentoMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      await entities.Documento.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentos'] });
      setDialogAberto(false);
      setDocumentoEditando(null);
      toast.success('Documento atualizado.');
    },
    onError: (err) => {
      toast.error(getDisplayMessage(err, 'Não foi possível atualizar o documento. Tente novamente.'));
    }
  });

  const deleteDocumentoMutation = useMutation({
    mutationFn: async (id) => {
      await entities.Documento.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentos'] });
    }
  });

  const deleteDocumentosMutation = useMutation({
    mutationFn: async (ids) => {
      await Promise.all(ids.map(id => entities.Documento.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentos'] });
      setDocumentosSelecionados([]);
      setDialogExclusaoAberto(false);
    }
  });

  const handleAbrirDialog = (documento = null) => {
    setDocumentoEditando(documento);
    setDialogAberto(true);
  };

  const handleSalvarDocumento = (data) => {
    if (documentoEditando) {
      updateDocumentoMutation.mutate({ id: documentoEditando.id, data });
    } else {
      createDocumentoMutation.mutate(data);
    }
  };

  const handleExcluirDocumento = (id) => {
    if (confirm("Tem certeza que deseja excluir este documento?")) {
      deleteDocumentoMutation.mutate(id);
    }
  };

  const handleToggleSelecao = (documentoId) => {
    setDocumentosSelecionados(prev =>
      prev.includes(documentoId)
        ? prev.filter(id => id !== documentoId)
        : [...prev, documentoId]
    );
  };

  const handleSelecionarTodos = () => {
    if (documentosSelecionados.length === documentosFiltrados.length) {
      setDocumentosSelecionados([]);
    } else {
      setDocumentosSelecionados(documentosFiltrados.map(d => d.id));
    }
  };

  const handleDownloadSelecionados = async () => {
    const docsSelecionados = documentosFiltrados.filter(d => 
      documentosSelecionados.includes(d.id) && d.anexo_url && d.anexo_url.trim() !== ''
    );

    const totalSelecionados = documentosSelecionados.length;
    const semAnexo = totalSelecionados - docsSelecionados.length;

    if (docsSelecionados.length === 0) {
      if (semAnexo > 0) {
        alert(`Os ${semAnexo} documento(s) selecionado(s) não possuem anexo.`);
      } else {
        alert("Nenhum documento selecionado.");
      }
      return;
    }

    if (semAnexo > 0) {
      if (!confirm(`${semAnexo} documento(s) sem anexo serão ignorados.\n\nDeseja continuar com o download de ${docsSelecionados.length} arquivo(s)?`)) {
        return;
      }
    }

    setDownloadingBatch(true);

    try {
      // Baixar em paralelo com limite de requisições simultâneas
      const BATCH_SIZE = 3;
      let sucessos = 0;
      let falhas = 0;

      for (let i = 0; i < docsSelecionados.length; i += BATCH_SIZE) {
        const batch = docsSelecionados.slice(i, i + BATCH_SIZE);
        
        await Promise.all(batch.map(async (doc) => {
          try {
            const url = doc.anexo_url;
            const blob = requiresUploadBlobAuth(url)
              ? await fetchUploadBlob(url)
              : await (async () => {
                  const res = await fetch(url, { credentials: "include", mode: "cors" });
                  if (!res.ok) throw new Error(`HTTP ${res.status}`);
                  return res.blob();
                })();
            const blobUrl = window.URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.style.display = 'none';
            link.href = blobUrl;
            link.download = doc.anexo_nome || `documento_${doc.descricao}`;
            
            document.body.appendChild(link);
            link.click();
            
            await new Promise(resolve => setTimeout(resolve, 100));
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            
            sucessos++;
          } catch (error) {
            console.error(`Erro ao baixar ${doc.anexo_nome}:`, error);
            falhas++;
          }
        }));
        
        // Pequeno delay entre batches
        if (i + BATCH_SIZE < docsSelecionados.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      if (sucessos > 0) {
        alert(`✅ ${sucessos} arquivo(s) baixado(s) com sucesso!${falhas > 0 ? `\n⚠️ ${falhas} arquivo(s) falharam.` : ''}`);
      } else {
        alert("❌ Não foi possível baixar nenhum arquivo. Tente novamente.");
      }
    } catch (error) {
      console.error("Erro ao fazer download:", error);
      toast.error(getDisplayMessage(error, "Erro ao fazer download dos documentos. Tente novamente."), { duration: 5000 });
    } finally {
      setDownloadingBatch(false);
    }
  };

  const documentosFiltrados = safeDocumentos.filter(doc => {
    const matchSearch = doc.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       doc.tipo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       doc.categoria?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       doc.conteudo_extraido?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchStatus = filtroStatus === "todos" || doc.status === filtroStatus;
    const matchCategoria = filtroCategoria === "todos" || doc.categoria === filtroCategoria;
    
    return matchSearch && matchStatus && matchCategoria;
  });

  const estatisticas = {
    total: safeDocumentos.length,
    validos: safeDocumentos.filter(d => d.status === "valido").length,
    proximoVencimento: safeDocumentos.filter(d => d.status === "proximo_vencimento").length,
    vencidos: safeDocumentos.filter(d => d.status === "vencido").length
  };

  const getConfigByTipo = (tipoId) =>
    (Array.isArray(renovacaoConfigs) ? renovacaoConfigs : []).find((cfg) => cfg.documentoTipoId === tipoId);

  return (
    <div className="p-4 md:p-8 min-h-screen" data-tour="tour-Documentos-root">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Gestão de Documentos
            </h1>
            <p className="text-slate-600">
              Gerencie certidões e documentos da empresa e sócios.
            </p>
            <p className="text-slate-500 text-sm mt-1 max-w-2xl">
              Avisos por e-mail (vencimento em até 10 dias) são disparados ao carregar esta página, uma vez por documento,
              se houver destinatários cadastrados e envio de e-mail ativo no servidor.
            </p>
          </div>
          <Button onClick={() => handleAbrirDialog()} className="bg-blue-600 hover:bg-blue-700 shrink-0" data-tour="tour-Documentos-primary">
            <Plus className="w-4 h-4 mr-2" />
            Novo Documento
          </Button>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-none shadow-md bg-white/90">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total</p>
                  <p className="text-2xl font-bold text-slate-900">{estatisticas.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-md bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-green-700">Válidos</p>
                  <p className="text-2xl font-bold text-green-900">{estatisticas.validos}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-md bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-amber-700">Próx. Vencimento</p>
                  <p className="text-2xl font-bold text-amber-900">{estatisticas.proximoVencimento}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-md bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-red-700">Vencidos</p>
                  <p className="text-2xl font-bold text-red-900">{estatisticas.vencidos}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm mb-6">
          <CardContent className="p-6 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Renovação automática</h2>
                <p className="text-sm text-slate-600">
                  Ative por tipo oficial e execute o ciclo quando precisar. As opções técnicas ficam em avançado.
                </p>
                {!isAdmin && (
                  <p className="text-xs text-slate-500 mt-1">
                    Configurações técnicas gerenciadas pelo administrador.
                  </p>
                )}
              </div>
              <Button
                onClick={() => dispararRenovacaoMutation.mutate()}
                disabled={dispararRenovacaoMutation.isPending}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {dispararRenovacaoMutation.isPending ? "Executando..." : "Executar ciclo agora"}
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              {autoTiposCatalogo.map((tipo) => {
                const cfg = getConfigByTipo(tipo.codigo);
                return (
                  <div key={tipo.codigo} className="border rounded-lg p-3 bg-slate-50/70">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{tipo.nome}</p>
                        <p className="text-xs text-slate-500">
                          Modo: {cfg?.ativo ? "renovação automática ativa" : "renovação automática inativa"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={!!cfg?.ativo}
                          onCheckedChange={(checked) =>
                            updateRenovacaoConfigMutation.mutate({
                              documentoTipoId: tipo.codigo,
                              payload: { ativo: !!checked },
                            })
                          }
                        />
                        <span className="text-xs text-slate-700">{cfg?.ativo ? "Ativo" : "Inativo"}</span>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          onClick={() =>
                            setAdvancedByTipo((prev) => ({
                              ...prev,
                              [tipo.codigo]: !prev[tipo.codigo],
                            }))
                          }
                        >
                          {advancedByTipo[tipo.codigo] ? "Ocultar configurações avançadas" : "Mostrar configurações avançadas"}
                        </Button>

                        {advancedByTipo[tipo.codigo] && (
                          <div className="mt-3 space-y-2">
                            <p className="text-xs text-slate-500">
                              Uso técnico (admin): timeout, tentativas, endpoint e credencial.
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                type="number"
                                min="5"
                                placeholder="Timeout (ms)"
                                defaultValue={cfg?.timeoutMs ?? ""}
                                onBlur={(e) => {
                                  const value = e.target.value ? Number(e.target.value) : null;
                                  updateRenovacaoConfigMutation.mutate({
                                    documentoTipoId: tipo.codigo,
                                    payload: { timeout_ms: value },
                                  });
                                }}
                              />
                              <Input
                                type="number"
                                min="1"
                                placeholder="Tentativas"
                                defaultValue={cfg?.maxTentativas ?? 3}
                                onBlur={(e) => {
                                  const value = e.target.value ? Number(e.target.value) : 3;
                                  updateRenovacaoConfigMutation.mutate({
                                    documentoTipoId: tipo.codigo,
                                    payload: { max_tentativas: value },
                                  });
                                }}
                              />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <Input
                                type="url"
                                placeholder="Endpoint/API URL de emissão"
                                defaultValue={cfg?.parametros?.endpoint || ""}
                                onBlur={(e) => {
                                  const endpoint = e.target.value?.trim() || null;
                                  updateRenovacaoConfigMutation.mutate({
                                    documentoTipoId: tipo.codigo,
                                    payload: {
                                      parametros: {
                                        ...(cfg?.parametros && typeof cfg.parametros === "object" ? cfg.parametros : {}),
                                        endpoint,
                                      },
                                    },
                                  });
                                }}
                              />
                              <Input
                                type="password"
                                placeholder="Token/credencial (salvo criptografado)"
                                onBlur={(e) => {
                                  const token = e.target.value?.trim();
                                  if (!token) return;
                                  updateRenovacaoConfigMutation.mutate({
                                    documentoTipoId: tipo.codigo,
                                    payload: {
                                      credenciais: { token },
                                    },
                                  });
                                  e.target.value = "";
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-900">Últimas execuções</p>
              {Array.isArray(renovacaoExecucoes) && renovacaoExecucoes.length > 0 ? (
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {renovacaoExecucoes.map((exec) => (
                    <div key={exec.id} className="border rounded-lg p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-slate-900 truncate">
                          {exec.documento?.descricao || exec.documentoTipoId}
                        </p>
                        <p className="text-xs text-slate-500">
                          Status: {exec.status} | Tentativa {exec.tentativa}/{exec.maxTentativas}
                        </p>
                        {isAdmin && exec.erroMensagem && (
                          <p className="text-xs text-red-600 truncate">{exec.erroMensagem}</p>
                        )}
                      </div>
                      {isAdmin && (exec.status === "falha" || exec.status === "intervencao_humana") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => reprocessarExecucaoMutation.mutate(exec.id)}
                          disabled={reprocessarExecucaoMutation.isPending}
                        >
                          Reprocessar
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Sem execuções recentes de renovação automática.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Filtros */}
        <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  placeholder="Buscar por descrição, tipo, categoria ou conteúdo do documento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12"
                />
              </div>

              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <div className="flex flex-wrap gap-4">
                  <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                    <SelectTrigger className="w-full md:w-48">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos os Status</SelectItem>
                      <SelectItem value="valido">Válidos</SelectItem>
                      <SelectItem value="proximo_vencimento">Próx. Vencimento</SelectItem>
                      <SelectItem value="vencido">Vencidos</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
                    <SelectTrigger className="w-full md:w-48">
                      <SelectValue placeholder="Categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todas Categorias</SelectItem>
                      <SelectItem value="Empresa">Empresa</SelectItem>
                      <SelectItem value="Sócio">Sócio</SelectItem>
                      <SelectItem value="Certidões">Certidões</SelectItem>
                      <SelectItem value="Atestados de Capacidade">Atestados de Capacidade</SelectItem>
                      <SelectItem value="Certificados">Certificados</SelectItem>
                      <SelectItem value="Outros">Outros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {documentosFiltrados.length > 0 && (
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelecionarTodos}
                      className="whitespace-nowrap"
                    >
                      {documentosSelecionados.length === documentosFiltrados.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                    </Button>
                    
                    {documentosSelecionados.length > 0 && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDownloadSelecionados}
                          disabled={downloadingBatch}
                          className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100 whitespace-nowrap"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {downloadingBatch ? 'Baixando...' : `Baixar (${documentosSelecionados.length})`}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setDialogExclusaoAberto(true)}
                          className="whitespace-nowrap"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Excluir ({documentosSelecionados.length})
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lista de documentos */}
        <div className="grid gap-4">
          {isLoading ? (
            Array(3).fill(0).map((_, i) => (
              <Card key={i} className="border-none shadow-md animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))
          ) : documentosFiltrados.length === 0 ? (
            <Card className="border-none shadow-md">
              <CardContent className="p-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 text-lg mb-2">Nenhum documento encontrado</p>
                <p className="text-slate-400 text-sm">
                  {searchTerm ? 'Tente ajustar os filtros de busca' : 'Cadastre o primeiro documento clicando no botão acima'}
                </p>
              </CardContent>
            </Card>
          ) : (
            documentosFiltrados.map((documento) => (
              <div key={documento.id} className="flex items-start gap-3">
                <Checkbox
                  checked={documentosSelecionados.includes(documento.id)}
                  onCheckedChange={() => handleToggleSelecao(documento.id)}
                  className="mt-6"
                />
                <div className="flex-1">
                  <DocumentoCard
                    documento={documento}
                    onEditar={() => handleAbrirDialog(documento)}
                    onExcluir={() => handleExcluirDocumento(documento.id)}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="dialog-documento-desc">
          <DialogHeader>
            <DialogTitle>
              {documentoEditando ? 'Editar Documento' : 'Novo Documento'}
            </DialogTitle>
            <DialogDescription id="dialog-documento-desc">
              {documentoEditando ? 'Altere os dados do documento e salve.' : 'Preencha os dados e anexe o arquivo do documento.'}
            </DialogDescription>
          </DialogHeader>
          <DocumentoForm
            key={documentoEditando?.id ?? 'novo'}
            documento={documentoEditando}
            onSalvar={handleSalvarDocumento}
            onCancelar={() => setDialogAberto(false)}
            isSaving={createDocumentoMutation.isPending || updateDocumentoMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={dialogExclusaoAberto} onOpenChange={setDialogExclusaoAberto}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir {documentosSelecionados.length} documento(s) selecionado(s)?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteDocumentosMutation.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteDocumentosMutation.mutate(documentosSelecionados)}
              disabled={deleteDocumentosMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteDocumentosMutation.isPending ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}